import React, { memo, useMemo } from "react";
import { Box, styled } from "@mui/material";
import { tooltipClasses } from '@mui/material/Tooltip';
import Tooltip from '@mui/material/Tooltip';
// import LightTooltip from "@mui/material/Tooltip";

// --- Styled Tooltip for the "White Box" look ---
export const LightTooltip = styled(({ className, interactive, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: theme.shadows[1],
        fontSize: 11,
        maxWidth: 500,
        border: '1px solid #dadde9',
        padding: 0
    },
}));

// --- 1. The Mini Table Component (Popup Content) ---
export const PopupTable = ({ details }) => {
    // Check if Payable COA has ANY data 
    const hasPayable = details.some(d => d.LiabilityAccName && d.LiabilityAccName.trim() !== "");

    return (
        <Box sx={{ p: 1 }}>
            <Box sx={{
                maxHeight: 250,
                overflowY: "auto",
                "&::-webkit-scrollbar": { width: 5 },
                "&::-webkit-scrollbar-thumb": { backgroundColor: "#bdbdbd", borderRadius: 10 }
            }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '300px' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5', boxShadow: '0 1px 0 0 #e0e0e0' }}>
                        <tr>
                            <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', color: '#666' }}>Department</th>
                            <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', color: '#666' }}>Job Title</th>
                            <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', color: '#666' }}>Account</th>
                            {hasPayable && (
                                <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', color: '#666' }}>
                                    Payable Coa
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {details.map((row, index) => (
                            <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                <td style={{ padding: '6px 8px', fontSize: '12px' }}>{row.DepartmentName || ''}</td>
                                <td style={{ padding: '6px 8px', fontSize: '12px' }}>{row.JobTitle || ''}</td>
                                <td style={{ padding: '6px 8px', fontSize: '12px' }}>{row.AccountName || ''}</td>
                                {hasPayable && (
                                    <td style={{ padding: '6px 8px', fontSize: '12px' }}>
                                        {row.LiabilityAccName && row.LiabilityAccName.trim() !== "" ? row.LiabilityAccName : ''}
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Box>
        </Box>
    );
};

// --- 2. Profit Center Popup Table Component ---
export const ProfitCenterPopupTable = ({ pcNames }) => {
    const list = useMemo(() => {
        if (!pcNames) return [];
        if (Array.isArray(pcNames)) {
            return pcNames
                .map(p => (typeof p === 'object' && p !== null ? (p.PCName || p.pcName || p.ProfitCenterName || p.Name || p.label || String(p)) : String(p)))
                .map(s => s.trim())
                .filter(Boolean);
        }
        if (typeof pcNames === 'string') {
            return pcNames
                .split(',')
                .map(s => s.trim())
                .filter(Boolean);
        }
        return [String(pcNames).trim()].filter(Boolean);
    }, [pcNames]);

    if (list.length === 0) return null;

    return (
        <Box sx={{ p: 1 }}>
            <Box sx={{
                maxHeight: 250,
                overflowY: "auto",
                overflowX: "auto",
                "&::-webkit-scrollbar": { width: 5, height: 5 },
                "&::-webkit-scrollbar-thumb": { backgroundColor: "#bdbdbd", borderRadius: 10 }
            }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '200px' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f5f5f5', boxShadow: '0 1px 0 0 #e0e0e0' }}>
                        <tr>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '12px', color: '#666', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                Profit Centers
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((item, index) => (
                            <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                <td style={{ padding: '6px 12px', fontSize: '12px', color: '#333', whiteSpace: 'nowrap' }}>
                                    {item}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Box>
        </Box>
    );
};

// --- 3. The Profit Center Renderer Component ---
export const RenderProfitCenterMultiple = memo(({ pcNames, label = "Multiple" }) => {
    return (
        <LightTooltip
            title={<ProfitCenterPopupTable pcNames={pcNames} />}
            placement="bottom-start"
            interactive
        >
            <span
                style={{
                    color: "#4a90e2",
                    cursor: "pointer",
                    fontWeight: 500,
                    fontSize: "0.875rem"
                }}
            >
                {label}
            </span>
        </LightTooltip>
    );
});

// --- 2. The Renderer Component (Decides "Multiple" vs "Single") ---
export const RenderMultiple = memo(({ details, field }) => {
    const values = useMemo(() => {
        if (!details || details.length === 0) return [];

        const fieldMap = {
            dept: "DepartmentName",
            job: "JobTitle",
            acct: "AccountName",
            liab: "LiabilityAccName"
        };

        const key = fieldMap[field];
        return details
            .map(d => d[key])
            .filter(v => v && v.trim() !== "");
    }, [details, field]);

    if (values.length === 0) return " ";
    if (values.length === 1) return <span title={values[0]}>{values[0]}</span>;

    return (
        <LightTooltip
            title={<PopupTable details={details} />}
            placement="bottom-start"
            interactive
        >
            <span
                style={{
                    color: "#4a90e2",
                    cursor: "pointer",
                    fontWeight: 500,
                    fontSize: "0.875rem"
                }}
            >
                Multiple
            </span>
        </LightTooltip>
    );
});